import { useState, type MouseEvent } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ProgramNewsUpdate } from "@/services/adminProgramNews";
import EventBadges from "./EventBadges";
import EventDetailSheet from "./EventDetailSheet";
import { REASON_NOT_RECORDED } from "./glossary";
import EventSourceLinks from "./EventSourceLinks";
import { formatDate, formatDateTime } from "./formatters";
import ReviewedBadge from "./ReviewedBadge";
import { formatSchoolName, splitTeamId } from "./teamLabel";

interface EventCardProps {
  update: ProgramNewsUpdate;
  /** Called after a review is saved or removed so the list can refresh. */
  onReviewChanged?: () => void;
}

const noop = () => {};

const EventCard = ({ update, onReviewChanged = noop }: EventCardProps) => {
  const [open, setOpen] = useState(false);
  const { slug, gender } = splitTeamId(update.teamId);
  const people = (update.people ?? []).filter(person => person && person.name);

  return (
    <article
      className="cursor-pointer rounded-md border p-3 space-y-2 hover:bg-muted/30"
      onClick={(event: MouseEvent<HTMLElement>) => {
        // Links and buttons inside the card keep their own behaviour.
        if (!(event.target as HTMLElement).closest("a,button")) setOpen(true);
      }}
    >
      <div className="flex flex-wrap items-center gap-1 text-xs">
        <EventBadges update={update} />
        <ReviewedBadge verdict={update.myVerdict} />
        <span className="ml-auto text-muted-foreground" title={update.teamId}>
          {formatSchoolName(slug)}
          {gender ? ` · ${gender}` : ""}
        </span>
      </div>
      <h4 className="font-medium">{update.title}</h4>
      {update.summary ? <p className="text-sm text-muted-foreground">{update.summary}</p> : null}
      {update.rationale ? (
        <p className="text-sm">
          <span className="font-medium">Why:</span> {update.rationale}
        </p>
      ) : (
        <p className="text-xs italic text-muted-foreground">{REASON_NOT_RECORDED}</p>
      )}
      {people.length ? (
        <div className="flex flex-wrap gap-1">
          {people.map((person, index) => (
            <Badge key={`${person.name}-${index}`} variant="outline" className="font-normal">
              {person.role ? `${person.name} · ${person.role}` : person.name}
            </Badge>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Event date: {formatDate(update.eventDate)}</span>
        <span>Discovered: {formatDateTime(update.discoveredAt)}</span>
        {update.status ? <span>Status: {update.status}</span> : null}
      </div>
      <EventSourceLinks sourceItemIds={update.sourceItemIds} />
      <Button
        type="button"
        size="sm"
        variant="outline"
        aria-label={`Review ${update.title}`}
        onClick={() => setOpen(true)}
      >
        Review
      </Button>
      {open ? (
        <EventDetailSheet
          update={update}
          open={open}
          onOpenChange={setOpen}
          onReviewChanged={onReviewChanged}
        />
      ) : null}
    </article>
  );
};

export default EventCard;
